import React, { useRef, useEffect, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import { loadEvents } from "./lib/events";
import "./Map.css";

// ── Date helpers ──
function toYMDFromSlash(str) {
  const parts = String(str).split("/");
  if (parts.length !== 3) return "";
  const [m, d, y] = parts.map(Number);
  if (!y || !m || !d) return "";
  return `${y}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
}
function toYMD(str) {
  const s = String(str || "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (s.includes("/")) return toYMDFromSlash(s);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  return "";
}
function todayLocalYMD() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}-${String(n.getDate()).padStart(2,"0")}`;
}
function formatDate(dateStr) {
  const ymd = toYMD(dateStr);
  if (!ymd) return dateStr;
  const [y, m, d] = ymd.split("-");
  const months = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  return `${parseInt(d)} ${months[parseInt(m)-1]} ${y}`;
}

mapboxgl.accessToken = "pk.eyJ1IjoiZWVzcGRldiIsImEiOiJjbHZuaHJ4dW0wZHNmMmlubnpscXNzOGc4In0.z0wGQ-JbOcgaMEucU-NEHA";

const EU_CITIES = ["barcelona","madrid","lisboa","lisbon","london","paris","berlin","amsterdam","rome","milan","vienna","zurich","prague","warsaw","brussels","budapest"];
const EU_COUNTRIES = ["spain","portugal","france","italy","germany","austria","switzerland","netherlands","poland","belgium","czech republic","hungary","ireland","uk","united kingdom"];
const isEurope = (city="", country="") => {
  const c = city.toLowerCase(), co = country.toLowerCase();
  return EU_CITIES.some(x => c.includes(x)) || EU_COUNTRIES.some(x => co.includes(x));
};

const Map = () => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const markersRef = useRef({});
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = todayLocalYMD();

  // Load events
  useEffect(() => {
    loadEvents()
      .then(data => {
        if (!data) { setLoading(false); return; }
        const items = (data.features || []).map((f, i) => ({
          id: i,
          ...f.properties,
          coordinates: f.geometry.coordinates,
          ymd: toYMD(f.properties.date) || "",
          isPast: (toYMD(f.properties.date) || "") < today,
        })).sort((a, b) => a.ymd.localeCompare(b.ymd));
        setEvents(items);
        const first = items.find(e => !e.isPast);
        if (first) setSelected(first.id);
      })
      .catch(e => console.error("Events load error:", e))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  // Init map
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/dark-v10",
      center: [-99.1332, 19.4326],
      zoom: 4,
      attributionControl: false,
      dragRotate: false,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;
    popupRef.current = new mapboxgl.Popup({
      offset: 28,
      closeButton: true,
      closeOnClick: false,
      className: "app-popup",
    });
    return () => map.remove();
  }, []);

  // Add markers once events are loaded
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !events.length) return;

    const addMarkers = () => {
      events.forEach(ev => {
        const el = document.createElement("div");
        el.className = `ck-marker${ev.isPast ? " ck-marker--past" : ""}`;
        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat(ev.coordinates)
          .addTo(map);
        markersRef.current[ev.id] = marker;
        el.addEventListener("click", e => {
          e.stopPropagation();
          setSelected(ev.id);
          flyTo(ev);
          showPopup(ev);
        });
      });
    };

    if (map.isStyleLoaded()) addMarkers();
    else map.once("load", addMarkers);
  }, [events]); // eslint-disable-line

  const flyTo = useCallback((ev) => {
    mapRef.current?.flyTo({ center: ev.coordinates, zoom: 12, duration: 900 });
  }, []);

  const showPopup = useCallback((ev) => {
    const popup = popupRef.current;
    const map = mapRef.current;
    if (!popup || !map) return;
    const europe = isEurope(ev.city, ev.country);
    const tickets = !ev.isPast && ev.ticket_url
      ? `<a class="popup-button" href="${ev.ticket_url}" target="_blank" rel="noopener noreferrer">🎟 Comprar Boletos</a>`
      : "";
    const vip = !ev.isPast && ev.vipticket_url && !europe
      ? `<a class="popup-button popup-button--vip" href="${ev.vipticket_url}" target="_blank" rel="noopener noreferrer">⭐ VIP</a>`
      : "";
    const eticketInfo = ev.ticket_url && ev.ticket_url.includes("eticket.mx")
      ? `<img src="/assets/eticket-info.jpeg" alt="Información Eticket" loading="lazy" style="margin-top:10px;border-radius:6px;max-width:100%;height:auto;" />`
      : "";
    popup.setLngLat(ev.coordinates).setHTML(`
      <div class="popup-content">
        ${ev.image_url ? `<div class="popup-image-container"><img src="${ev.image_url}" alt="${ev.title}" loading="lazy"/></div>` : ""}
        <div class="popup-inner">
          <h3 class="popup-title">${ev.title}</h3>
          <p class="popup-city">${ev.city || ""}</p>
          <p class="popup-date"><strong style="color:#f0f0f0;">Fecha:</strong> ${formatDate(ev.date)}</p>
          ${ev.isPast ? `<span class="popup-badge">Evento pasado</span>` : ""}
          <div class="popup-actions">${tickets}${vip}</div>
          ${eticketInfo}
        </div>
      </div>`);
    markersRef.current[ev.id]?.setPopup(popup);
    markersRef.current[ev.id]?.togglePopup();
  }, []);

  const handleSelect = useCallback((ev) => {
    setSelected(ev.id);
    flyTo(ev);
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    if (!isMobile) {
      showPopup(ev);
    } else {
      // Mobile sheet
      const existing = document.querySelector(".mobile-popup-overlay");
      if (existing) existing.remove();
      const europe = isEurope(ev.city, ev.country);
      const tickets = !ev.isPast && ev.ticket_url
        ? `<a class="popup-button" href="${ev.ticket_url}" target="_blank" rel="noopener noreferrer">🎟 Comprar Boletos</a>`
        : "";
      const vip = !ev.isPast && ev.vipticket_url && !europe
        ? `<a class="popup-button popup-button--vip" href="${ev.vipticket_url}" target="_blank" rel="noopener noreferrer">⭐ VIP</a>`
        : "";
      const overlay = document.createElement("div");
      overlay.className = "mobile-popup-overlay";
      overlay.innerHTML = `
        <div class="mobile-popup">
          <button class="popup-close" aria-label="Cerrar">&times;</button>
          <div class="popup-content">
            ${ev.image_url ? `<div class="popup-image-container"><img src="${ev.image_url}" alt="${ev.title}" loading="lazy"/></div>` : ""}
            <div class="popup-inner">
              <h3 class="popup-title">${ev.title}</h3>
              <p class="popup-city">${ev.city || ""}</p>
              <p class="popup-date"><strong style="color:#f0f0f0;">Fecha:</strong> ${formatDate(ev.date)}</p>
              ${ev.isPast ? `<span class="popup-badge">Evento pasado</span>` : ""}
              <div class="popup-actions">${tickets}${vip}</div>
            </div>
          </div>
        </div>`;
      document.body.appendChild(overlay);
      overlay.querySelector(".popup-close").addEventListener("click", () => overlay.remove());
      overlay.addEventListener("click", e => { if (e.target === overlay) overlay.remove(); });
    }
  }, [flyTo, showPopup]);

  const upcoming = events.filter(e => !e.isPast);
  const past = events.filter(e => e.isPast);

  return (
    <div className="map-page">
      {/* ── Sidebar ── */}
      <aside className="map-sidebar">
        <div className="map-sidebar__header">
          <h1 className="map-sidebar__title">Próximos Eventos</h1>
          <p className="map-sidebar__sub">{upcoming.length} show{upcoming.length !== 1 ? "s" : ""} disponibles</p>
        </div>

        <div className="map-sidebar__list">
          {loading && <p className="map-sidebar__status">Cargando eventos…</p>}

          {!loading && upcoming.length === 0 && (
            <p className="map-sidebar__status">Sin eventos próximos.</p>
          )}

          {upcoming.map(ev => (
            <button
              key={ev.id}
              className={`map-event-item${selected === ev.id ? " map-event-item--active" : ""}`}
              onClick={() => handleSelect(ev)}
            >
              {ev.image_url && (
                <img src={ev.image_url} alt={ev.title} className="map-event-item__img" loading="lazy" />
              )}
              <div className="map-event-item__info">
                <span className="map-event-item__date">{formatDate(ev.date)}</span>
                <span className="map-event-item__title">{ev.title}</span>
                <span className="map-event-item__city">{ev.city}</span>
              </div>
              {ev.ticket_url && (
                <a
                  href={ev.ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-event-item__ticket"
                  onClick={e => e.stopPropagation()}
                >
                  Boletos
                </a>
              )}
            </button>
          ))}

          {past.length > 0 && (
            <>
              <p className="map-sidebar__section-label">Eventos pasados</p>
              {past.map(ev => (
                <button
                  key={ev.id}
                  className="map-event-item map-event-item--past"
                  onClick={() => handleSelect(ev)}
                >
                  <div className="map-event-item__info">
                    <span className="map-event-item__date">{formatDate(ev.date)}</span>
                    <span className="map-event-item__title">{ev.title}</span>
                    <span className="map-event-item__city">{ev.city}</span>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      </aside>

      {/* ── Map ── */}
      <div className="map-canvas" ref={mapContainerRef} />
    </div>
  );
};

export default Map;
